import { Component } from '@theme/component';

/**
 * Announcement banner custom element that allows fading between content.
 * Based on the Slideshow component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} slideshowContainer
 * @property {HTMLElement[]} [slides]
 * @property {HTMLButtonElement} [previous]
 * @property {HTMLButtonElement} [next]
 *
 * @extends {Component<Refs>}
 */
export class AnnouncementBar extends Component {
  #current = 0;

  /**
   * The interval ID for automatic playback.
   * @type {number|undefined}
   */
  #interval = undefined;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('mouseenter', this.suspend);
    this.addEventListener('mouseleave', this.resume);
    document.addEventListener('visibilitychange', this.#handleVisibilityChange);

    this.play();
    this.#setLastSiteUpdate();
  }

  async #setLastSiteUpdate() {
    const target = this.querySelector('[data-last-site-update]');
    if (!target) return;

    try {
      const response = await fetch('/products.json?limit=250', {
        headers: { Accept: 'application/json' },
        cache: 'no-store',
      });

      if (!response.ok) throw new Error('Unable to load product updates');

      const data = await response.json();
      const timestamps = (data.products ?? [])
        .map((product) => Date.parse(product.updated_at))
        .filter((timestamp) => Number.isFinite(timestamp));

      if (!timestamps.length) throw new Error('No product update timestamps');

      const latestUpdate = new Date(Math.max(...timestamps));
      const now = new Date();
      const yesterday = new Date(now.getTime() - 86400000);
      const dateKey = (date) =>
        new Intl.DateTimeFormat('sv-SE', {
          timeZone: 'Africa/Luanda',
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
        }).format(date);

      const time = new Intl.DateTimeFormat('pt-PT', {
        timeZone: 'Africa/Luanda',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      }).format(latestUpdate);

      let dateLabel;
      if (dateKey(latestUpdate) === dateKey(now)) {
        dateLabel = `hoje às ${time}`;
      } else if (dateKey(latestUpdate) === dateKey(yesterday)) {
        dateLabel = `ontem às ${time}`;
      } else {
        const date = new Intl.DateTimeFormat('pt-PT', {
          timeZone: 'Africa/Luanda',
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
        }).format(latestUpdate);
        dateLabel = `${date} às ${time}`;
      }

      target.textContent = `Última atualização do site: ${dateLabel}`;
    } catch {
      target.textContent = 'Stock atualizado regularmente';
    }
  }

  next() {
    this.current += 1;
  }

  previous() {
    this.current -= 1;
  }

  /**
   * Starts automatic slide playback.
   * @param {number} [interval] - The time interval in seconds between slides.
   */
  play(interval = this.autoplayInterval) {
    if (!this.autoplay) return;

    this.paused = false;

    this.#interval = setInterval(() => {
      if (this.matches(':hover') || document.hidden) return;

      this.next();
    }, interval);
  }

  /**
   * Pauses automatic slide playback.
   */
  pause() {
    this.paused = true;
    this.suspend();
  }

  get paused() {
    return this.hasAttribute('paused');
  }

  set paused(paused) {
    this.toggleAttribute('paused', paused);
  }

  /**
   * Suspends automatic slide playback.
   */
  suspend() {
    clearInterval(this.#interval);
    this.#interval = undefined;
  }

  /**
   * Resumes automatic slide playback if autoplay is enabled.
   */
  resume() {
    if (!this.autoplay || this.paused) return;

    this.pause();
    this.play();
  }

  get autoplay() {
    return Boolean(this.autoplayInterval);
  }

  get autoplayInterval() {
    const interval = this.getAttribute('autoplay');
    const value = parseInt(`${interval}`, 10);

    if (Number.isNaN(value)) return undefined;

    return value * 1000;
  }

  get current() {
    return this.#current;
  }

  set current(current) {
    this.#current = current;

    let relativeIndex = current % (this.refs.slides ?? []).length;
    if (relativeIndex < 0) {
      relativeIndex += (this.refs.slides ?? []).length;
    }

    this.refs.slides?.forEach((slide, index) => {
      slide.setAttribute('aria-hidden', `${index !== relativeIndex}`);
    });
  }

  /**
   * Pause the slideshow when the page is hidden.
   */
  #handleVisibilityChange = () => (document.hidden ? this.pause() : this.resume());
}

if (!customElements.get('announcement-bar-component')) {
  customElements.define('announcement-bar-component', AnnouncementBar);
}
